// disable accessibility rules for tooltips which are very special components

/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */

import React from 'react'
import ReactDOM from 'react-dom'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { Manager, Reference, Popper } from 'react-popper'
import { useIsMounted, useHandler, useResizeObserver } from 'src/hooks'
import styles from './Tooltip.scss'

const popperContainer = document.createElement('div')
popperContainer.setAttribute('id', 'popperContainer')
document.body.appendChild(popperContainer)

/**
 * A tooltip to display when its children are hovered.
 */

function Tooltip({
  popperProps,
  title,
  overlay,
  children,
  referenceElement,
  interactive,
  triggers,
  visible,
  disabled,
  wrapper: Wrapper,
  ...domProps
}) {
  // create local visibility state
  const [isVisible, setVisible] = React.useState(false)
  const hidden = React.useRef(true)

  // get isMounted handler
  const isMounted = useIsMounted()

  // create state updaters
  const show = useHandler(() => {
    if (isMounted()) {
      hidden.current = false
      setVisible(true)
    }
  })
  const hide = useHandler(() => {
    if (isMounted()) {
      hidden.current = true
      setVisible(false)
    }
  })

  // create triggers checkers
  const isTriggerHover = useHandler(() => triggers.includes('hover'))
  const isTriggerClick = useHandler(() => triggers.includes('click'))

  // hide and re-show if tooltip dimensions changes
  const [node, setNode] = React.useState({})
  const dimensions = React.useRef({})
  useResizeObserver(node, ([{ target }]) => {
    const { width, height } = target.getBoundingClientRect()
    if (!hidden.current && (width !== dimensions.current.width || height !== dimensions.current.height)) {
      dimensions.current = { width, height }
      hide()
      show()
    }
  })

  // create hover callbacks
  const onMouseEnter = useHandler(() => isTriggerHover() && show())
  const onMouseLeave = useHandler(() => isTriggerHover() && hide())

  // create click callback
  const onClick = useHandler(e => {
    if (!disabled && isTriggerClick()) {
      e.preventDefault()
      isVisible ? hide() : show()
    } else {
      // always hide tooltip on click to avoid remaining unwanted tooltips
      hide()
    }
  })

  // hide tooltip when disabled
  React.useEffect(
    () => {
      disabled && hide()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [disabled]
  )

  return (
    <Manager>
      {children && (
        <Reference>
          {({ ref }) => (
            <Wrapper
              ref={ref}
              className={classnames(styles.tooltip, domProps.className)}
              style={domProps.style}
              onClick={onClick}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
            >
              {children}
            </Wrapper>
          )}
        </Reference>
      )}
      {!disabled &&
        (isVisible || visible) &&
        (title || overlay) &&
        ReactDOM.createPortal(
          <Popper
            placement="auto"
            modifiers={{ preventOverflow: { boundariesElement: 'viewport' } }}
            {...popperProps}
            {...(referenceElement ? { referenceElement } : {})}
          >
            {({ ref, style, placement, arrowProps }) => (
              <>
                {interactive && isTriggerClick() && <div onClick={onClick} className={styles.mask} />}
                <div
                  ref={ref}
                  style={style}
                  data-placement={placement}
                  className={classnames(styles.popper, { [styles.interactive]: interactive })}
                  onMouseEnter={interactive ? onMouseEnter : undefined}
                  onMouseLeave={interactive ? onMouseLeave : undefined}
                  onClick={e => e.preventDefault() || e.stopPropagation()}
                >
                  <div
                    ref={current => current !== node.current && setNode({ current })}
                    className={classnames(styles.popperWrapper, { [styles.overlay]: overlay })}
                  >
                    {title || (overlay && overlay(hide))}
                  </div>
                  <div
                    ref={arrowProps.ref}
                    data-placement={placement}
                    className={styles.arrow}
                    style={arrowProps.style}
                  />
                </div>
              </>
            )}
          </Popper>,
          popperContainer
        )}
    </Manager>
  )
}

Tooltip.defaultProps = {
  popperProps: {},
  interactive: false,
  triggers: ['hover'],
  wrapper: 'div',
}

Tooltip.propTypes = {
  /**
   * The text which will be displayed in the tooltip.
   */
  title: PropTypes.node,
  /**
   * You can provide the tooltip content as a render props in the overlay property
   */
  overlay: PropTypes.func,
  /**
   * Props applied on the popper node
   * @see [react-popper](https://github.com/FezVrasta/react-popper) documentation
   */
  popperProps: PropTypes.object,
  /**
   * The children which will show the tooltip when hovered.
   */
  children: PropTypes.node,
  /**
   * Reference element if tooltip has no child
   */
  referenceElement: PropTypes.oneOfType([
    PropTypes.shape({
      getBoundingClientRect: PropTypes.func.isRequired,
    }),
    PropTypes.node,
  ]),
  /**
   * Make the tooltip visible or not.
   */
  visible: PropTypes.bool,
  /**
   * Whether or not the tooltip stay visible when hovered (when 'hover' trigger is enabled)
   */
  interactive: PropTypes.bool,
  /**
   * List of triggers
   * Can contain 'hover' or/and 'click'
   */
  triggers: PropTypes.arrayOf(PropTypes.string),
  /**
   * Whether to disable the tooltip.
   */
  disabled: PropTypes.bool,
  /**
   * The wrapper element of the tooltip children.
   */
  wrapper: PropTypes.elementType,
}

export default Tooltip
